import { generateClient } from 'aws-amplify/api';
import { watchlistItemsByWatchlistIDAndCreatedAt } from '../graphql/queries';

/**
 * Simple utility to get actual item counts for watchlists
 * This doesn't update the database, just calculates the correct counts
 */

/**
 * Gets the actual item count for a single watchlist
 * @param {string} watchlistId - The ID of the watchlist
 * @returns {Promise<number>} - The actual item count
 */
export const getActualItemCount = async (watchlistId) => {
  try {
    const client = generateClient();
    
    const result = await client.graphql({
      query: watchlistItemsByWatchlistIDAndCreatedAt,
      variables: {
        watchlistID: watchlistId,
        limit: 1000 // High limit to get all items
      }
    });
    
    return result.data?.watchlistItemsByWatchlistIDAndCreatedAt?.items?.length || 0;
  } catch (error) {
    console.error(`Error getting item count for watchlist ${watchlistId}:`, error);
    return 0;
  }
};

/**
 * Updates watchlists array with correct item counts
 * @param {Array} watchlists - Array of watchlist objects
 * @returns {Promise<Array>} - Watchlists with correct item counts
 */
export const enrichWatchlistsWithCounts = async (watchlists) => {
  if (!watchlists || watchlists.length === 0) {
    return [];
  }

  const enrichedWatchlists = await Promise.all(
    watchlists.map(async (watchlist) => {
      try {
        const actualCount = await getActualItemCount(watchlist.id);
        return {
          ...watchlist,
          itemCount: actualCount
        };
      } catch (error) {
        console.error(`Error enriching watchlist ${watchlist.id}:`, error);
        return {
          ...watchlist,
          itemCount: 0
        };
      }
    })
  );

  return enrichedWatchlists;
};

/**
 * Calculates total items across all watchlists
 * @param {Array} watchlists - Array of watchlist objects
 * @returns {number} - Total item count
 */
export const calculateTotalItems = (watchlists) => {
  if (!watchlists || watchlists.length === 0) {
    return 0;
  }
  
  return watchlists.reduce((total, watchlist) => {
    return total + (watchlist.itemCount || 0);
  }, 0);
};

/**
 * Gets item counts for multiple watchlists in parallel
 * @param {Array} watchlistIds - Array of watchlist IDs
 * @returns {Promise<Object>} - Object with watchlistId as key and count as value
 */
export const getBatchItemCounts = async (watchlistIds) => {
  if (!watchlistIds || watchlistIds.length === 0) {
    return {};
  }

  const countPromises = watchlistIds.map(async (id) => {
    const count = await getActualItemCount(id);
    return { id, count };
  });

  const results = await Promise.all(countPromises);
  
  return results.reduce((acc, { id, count }) => {
    acc[id] = count;
    return acc;
  }, {});
};